import React from 'react';
import { View } from 'react-native';

import { Button, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { AuthBrandLogo } from '@/features/auth/components/auth-brand-logo';
import { AuthPrimaryButton } from '@/features/auth/components/auth-primary-button';
import { AuthScreenBackground } from '@/features/auth/components/auth-screen-background';
import { createStyles, useScaling } from '@/hooks';
import { colors } from '@/theme/colors';

const strings = STRINGS.auth.sso.playersConsent;

type SsoPlayersConsentProps = {
  /** Guardian consents to sharing their children list (grants the `players` scope). */
  onAllow: () => void;
  /** Guardian declines — sign-in still completes, but without sharing the children list. */
  onDecline: () => void;
  /** Disable the actions while the choice is being submitted. */
  submitting?: boolean;
};

/**
 * Consent prompt for the OIDC `players` scope (the guardian's children list — sensitive child
 * data). The IdP flags this via `requiresPlayersConsent`; the guardian's choice is handed back
 * through `confirmInteraction(uid, sharePlayers)`. Declining still completes sign-in without the
 * scope, so we present it as Allow / Not now rather than a hard cancel (BE docs/tasks/019).
 */
export function SsoPlayersConsent({
  onAllow,
  onDecline,
  submitting = false,
}: SsoPlayersConsentProps) {
  const styles = useStyles();
  const { scale } = useScaling();

  return (
    <AuthScreenBackground scrollable>
      <View style={styles.container}>
        <View style={styles.card}>
          <AuthBrandLogo portrait width={scale(154)} height={scale(84)} />
          <Text variant="Font Guardian App/Heading/Heading H6 18px" style={styles.title}>
            {strings.title}
          </Text>
          <Text variant="Font Guardian App/Subtitle/Subtitle 14 Regular" style={styles.body}>
            {strings.description}
          </Text>

          <View style={styles.list}>
            {strings.sharedItems.map(item => (
              <View key={item} style={styles.listItem}>
                <View style={styles.bullet} />
                <Text
                  variant="Font Guardian App/Subtitle/Subtitle 14 Regular"
                  style={styles.listText}>
                  {item}
                </Text>
              </View>
            ))}
          </View>

          <Text variant="Font Guardian App/Subtitle/Subtitle 12 Regular" style={styles.note}>
            {strings.note}
          </Text>

          <View style={styles.actions}>
            <AuthPrimaryButton onPress={onAllow} style={submitting ? styles.disabled : undefined}>
              {strings.allow}
            </AuthPrimaryButton>
            <Button
              variant="outline"
              onPress={onDecline}
              disabled={submitting}
              style={styles.declineButton}>
              <Button.Text variant="Font Guardian App/Subtitle/Subtitle 14 Semibold">
                {strings.decline}
              </Button.Text>
            </Button>
          </View>
        </View>
      </View>
    </AuthScreenBackground>
  );
}

const useStyles = createStyles(({ scale }) => ({
  container: {
    flex: 1,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    padding: scale(24),
  },
  card: {
    width: '100%',
    maxWidth: scale(440),
    backgroundColor: colors['0'],
    borderRadius: scale(16),
    padding: scale(24),
    alignItems: 'center',
    gap: scale(16),
  },
  title: {
    color: colors['10'],
    textAlign: 'center',
  },
  body: {
    color: colors['10'],
    textAlign: 'center',
  },
  list: {
    width: '100%',
    gap: scale(10),
  },
  listItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: scale(10),
  },
  bullet: {
    width: scale(6),
    height: scale(6),
    borderRadius: scale(3),
    backgroundColor: colors.Blue,
  },
  listText: {
    flex: 1,
    color: colors['10'],
  },
  note: {
    color: colors['5'],
    textAlign: 'center',
  },
  actions: {
    width: '100%',
    marginTop: scale(8),
    gap: scale(12),
  },
  declineButton: {
    width: '100%',
    borderRadius: scale(8),
  },
  disabled: {
    opacity: 0.6,
  },
}));

export default SsoPlayersConsent;
